import { PageWidgetType } from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import type {
  PageWidgetFlourish,
  PageWidgetFlourishLike,
} from '@efdigital/ll-addon-data/types/content/objects/page-widget-flourish';
import { selectValue } from '@efdigital/ll-addon-ui/modifiers/select-value';
import {
  type SizePreset,
  Sizing,
} from '@efdigital/ll-admin-v2/lib/media/sizes.ts';
import { uniqueId } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';

import ImagePicker from '../../pickers/image-picker.gts';
import type {
  PageWidgetComponentSignature,
  WidgetEditor,
} from '../page-widget-signature.ts';

const FLOURISH_TYPES: Record<PageWidgetFlourish['flourishType'], string> = {
  line: 'Line',
  dots: 'Dots',
};

const FLOURISH_COLORS: Record<PageWidgetFlourish['flourishColor'], string> = {
  primary: 'Primary',
  secondary: 'Secondary',
  default: 'Default',
};

const FLOURISH_IMAGE_POSITIONS: Record<
  PageWidgetFlourish['imagePosition'],
  string
> = {
  start: 'Start',
  end: 'End',
  center: 'Center',
  none: 'None',
};

const FLOURISH_TYPES_ARRAY = Object.entries(FLOURISH_TYPES).map(
  ([key, value]) => ({
    key,
    value,
  })
);

const FLOURISH_COLORS_ARRAY = Object.entries(FLOURISH_COLORS).map(
  ([key, value]) => ({
    key,
    value,
  })
);

const FLOURISH_IMAGE_POSITIONS_ARRAY = Object.entries(
  FLOURISH_IMAGE_POSITIONS
).map(([key, value]) => ({
  key,
  value,
}));

const FLOURISH_IMAGE_SIZE: SizePreset = {
  width: 300,
  height: 300,
  sizing: Sizing.Contain,
};

export class PageWidgetFlourishComponent extends Component<PageWidgetComponentSignature> {
  get widget() {
    return this.args.widget as PageWidgetFlourish;
  }

  setImage = (url: string) => {
    this.widget.image = url;
  };

  setImagePosition = (event: Event) => {
    const imagePosition = (event.target as HTMLSelectElement)
      .value as PageWidgetFlourish['imagePosition'];
    this.widget.imagePosition = imagePosition;
  };

  setFlourishType = (event: Event) => {
    const flourishType = (event.target as HTMLSelectElement)
      .value as PageWidgetFlourish['flourishType'];
    this.widget.flourishType = flourishType;
  };

  setFlourishColor = (event: Event) => {
    const flourishColor = (event.target as HTMLSelectElement)
      .value as PageWidgetFlourish['flourishColor'];
    this.widget.flourishColor = flourishColor;
  };

  get showImagePicker() {
    return this.widget.imagePosition !== 'none';
  }

  <template>
    {{#let (uniqueId) as |uniqueId|}}
      <div class="card-body">
        <div class="form-group mb-4">
          <label for="{{uniqueId}}-flourishType">Flourish type</label>
          <select
            class="form-control"
            id="{{uniqueId}}-flourishType"
            {{selectValue this.widget.flourishType}}
            {{on "change" this.setFlourishType}}
          >
            {{#each FLOURISH_TYPES_ARRAY as |flourishType|}}
              <option value={{flourishType.key}}>{{flourishType.value}}</option>
            {{/each}}
          </select>
        </div>
        <div class="form-group mb-4">
          <label for="{{uniqueId}}-flourishColor">Flourish color</label>
          <select
            class="form-control"
            id="{{uniqueId}}-flourishColor"
            {{selectValue this.widget.flourishColor}}
            {{on "change" this.setFlourishColor}}
          >
            {{#each FLOURISH_COLORS_ARRAY as |flourishColor|}}
              <option
                value={{flourishColor.key}}
              >{{flourishColor.value}}</option>
            {{/each}}
          </select>
        </div>
        <div class="form-group mb-4">
          <label for="{{uniqueId}}-imagePosition">Image position</label>
          <select
            class="form-control"
            id="{{uniqueId}}-imagePosition"
            {{selectValue this.widget.imagePosition}}
            {{on "change" this.setImagePosition}}
          >
            {{#each FLOURISH_IMAGE_POSITIONS_ARRAY as |imagePosition|}}
              <option
                value={{imagePosition.key}}
              >{{imagePosition.value}}</option>
            {{/each}}
          </select>
        </div>
        {{#if this.showImagePicker}}
          <div class="form-group mb-4">
            <label for="{{uniqueId}}-image">Image</label>
            <ImagePicker
              @value={{this.widget.image}}
              @onChange={{this.setImage}}
              @required={{false}}
              @sizeHint="custom"
              @customSize={{FLOURISH_IMAGE_SIZE}}
            />
          </div>
        {{/if}}
      </div>
    {{/let}}
  </template>
}

const DEFAULT_WIDGET_FLOURISH: Omit<PageWidgetFlourishLike, 'wid'> = {
  widgetType: PageWidgetType.Flourish,
  title: 'Divider',
  css: '',
  flourishType: 'line',
  flourishColor: 'primary',
  image: '',
  imagePosition: 'none',
};

export const FlourishWidgetEditor: WidgetEditor = {
  icon: 'ph:equals-bold',
  label: 'Divider',
  component: PageWidgetFlourishComponent,
  dataType: DataTypeContent.PageWidgetFlourish,
  widgetData: DEFAULT_WIDGET_FLOURISH,
};
